import type { ProjectSceneState } from "../projects/project-types";
import type {
  CargoEditorCargoAsset,
  CargoEditorRackCargoUpdate
} from "../scenes/cargo-editor-scene";
import { createProjectRackCargo, replaceRackCargoContents } from "./rack-cargo-bulk-state";

type CargoContents = Record<string, CargoEditorCargoAsset>;

export function applyBatchRackCargo(
  state: ProjectSceneState,
  updatesByRack: ReadonlyMap<string, CargoEditorRackCargoUpdate[]>
) {
  const racks = { ...state.racks };
  updatesByRack.forEach((updates, rackId) => {
    const rack = racks[rackId] ?? { cargo: {}, cargoCapacities: {} };
    racks[rackId] = { ...rack, cargo: createProjectRackCargo(updates) };
  });
  return { ...state, racks };
}

export function applyBatchRackCargoCapacity(
  state: ProjectSceneState,
  cellIdsByRack: ReadonlyMap<string, string[]>,
  cargoCapacity: number
) {
  const racks = { ...state.racks };
  cellIdsByRack.forEach((cellIds, rackId) => {
    const rack = racks[rackId] ?? { cargo: {}, cargoCapacities: {} };
    const selectedCellIds = new Set(cellIds);
    racks[rackId] = {
      ...rack,
      cargo: Object.fromEntries(
        Object.entries(rack.cargo).filter(([key]) => {
          const [cellId, , slotValue] = key.split("\u0000");
          return !selectedCellIds.has(cellId) || Number(slotValue) < cargoCapacity;
        })
      ),
      cargoCapacities: {
        ...rack.cargoCapacities,
        ...Object.fromEntries(cellIds.map((cellId) => [cellId, cargoCapacity]))
      }
    };
  });
  return { ...state, racks };
}

export function applyBatchRackLayerHeights(
  state: ProjectSceneState,
  rackIds: readonly string[],
  layerHeights: number[]
) {
  const selectedRackIds = new Set(rackIds);
  const racks = Object.fromEntries(
    Object.entries(state.racks).map(([rackId, rack]) => [
      rackId,
      selectedRackIds.has(rackId)
        ? {
            ...rack,
            cargo: Object.fromEntries(
              Object.entries(rack.cargo).filter(
                ([key]) => Number(key.split("\u0000")[1]) < layerHeights.length
              )
            ),
            layerHeightMode: "clear" as const,
            layerHeights: [...layerHeights]
          }
        : rack
    ])
  );
  rackIds.forEach((rackId) => {
    if (!racks[rackId]) {
      racks[rackId] = {
        cargo: {},
        cargoCapacities: {},
        layerHeightMode: "clear",
        layerHeights: [...layerHeights]
      };
    }
  });
  return { ...state, racks };
}

export function replaceBatchRackCargoContents(
  contents: CargoContents,
  updatesByRack: ReadonlyMap<string, CargoEditorRackCargoUpdate[]>
) {
  let next = contents;
  updatesByRack.forEach((updates, rackId) => {
    next = replaceRackCargoContents(next, rackId, updates);
  });
  return next;
}

export function pruneBatchRackCargoContents(
  contents: CargoContents,
  rackIds: readonly string[],
  options: { cargoCapacity?: number; layerCount?: number }
) {
  const selectedRackIds = new Set(rackIds);
  return Object.fromEntries(
    Object.entries(contents).filter(([key]) => {
      const [rackId, , heightValue, slotValue] = key.split("\u0000");
      if (!selectedRackIds.has(rackId)) {
        return true;
      }
      return (
        (options.layerCount === undefined || Number(heightValue) < options.layerCount) &&
        (options.cargoCapacity === undefined || Number(slotValue) < options.cargoCapacity)
      );
    })
  ) as CargoContents;
}

export function getProjectSceneStateByteLength(state: ProjectSceneState) {
  return new TextEncoder().encode(JSON.stringify(state)).byteLength;
}
